import type { TFunction } from 'i18next';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  completenessLabel,
  observedBalance,
  ReconciliationRunStatusBadge,
  snapshotBalance,
} from '@entities/reconciliation';
import type { ReconciliationFilterOption } from '@features/reconciliation';
import type { ReconciliationListPagination } from '@features/reconciliation/hooks/use-reconciliation-cursor-list';
import type { ReconciliationRunDto, ReconciliationSnapshotDto } from '@shared/api/contracts';
import { formatDate } from '@shared/lib/format-date';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { reconciliationRunRowsTableClass } from '../lib/table-classes';
import { ReconciliationAssetNetworkFilter } from './reconciliation-filter-fields';
import { ReconciliationListControls } from './reconciliation-list-controls';
import { ReconciliationTablePagination } from './reconciliation-table-pagination';
import { ReconciliationTableShell } from './reconciliation-table-shell';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  StatusBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/ui';

export type ReconciliationRunRowsFilterDraft = {
  assetCode?: string;
  networkCode?: string;
};

type ReconciliationRunDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: ReconciliationRunDto | undefined;
  isLoading: boolean;
  rows: ReconciliationSnapshotDto[];
  rowsLoading: boolean;
  rowsPagination: ReconciliationListPagination;
  onRowsPageChange: (page: number) => void;
  filterDraft: ReconciliationRunRowsFilterDraft;
  onAssetFilterChange: (assetCode?: string) => void;
  onNetworkFilterChange: (networkCode?: string) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  assetOptions: ReconciliationFilterOption[];
  networkOptionsForAsset: ReconciliationFilterOption[];
  currenciesLoading?: boolean;
  exportDisabled?: boolean;
  exportLoading?: boolean;
  onExport: () => void;
};

export function ReconciliationRunDetailDialog({
  open,
  onOpenChange,
  run,
  isLoading,
  rows,
  rowsLoading,
  rowsPagination,
  onRowsPageChange,
  filterDraft,
  onAssetFilterChange,
  onNetworkFilterChange,
  onApplyFilters,
  onResetFilters,
  assetOptions,
  networkOptionsForAsset,
  currenciesLoading = false,
  exportDisabled = false,
  exportLoading = false,
  onExport,
}: ReconciliationRunDetailDialogProps) {
  const { t } = useTranslation('cabinet');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] flex-col overflow-hidden sm:max-w-2xl lg:max-w-6xl"
      >
        <DialogHeader>
          <DialogTitle>{t('reconciliation.runDetail.title')}</DialogTitle>
          <DialogDescription>{t('reconciliation.runDetail.description')}</DialogDescription>
        </DialogHeader>

        {run ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{formatDate(run.reportDate)}</span>
            <span>· {t('reconciliation.runDetail.revision', { revision: run.revision })}</span>
            <ReconciliationRunStatusBadge status={run.status} />
          </div>
        ) : null}
        {run && (run.status === 'QUEUED' || run.status === 'RUNNING') ? (
          <StatusBanner>{t('reconciliation.runDetail.pending')}</StatusBanner>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={onExport}
            disabled={exportDisabled || exportLoading || !run}
          >
            <Download className="h-4 w-4" />
            {t('reconciliation.export.button')}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="shrink-0 space-y-1">
              <p className="text-sm font-medium text-foreground">{t('reconciliation.runDetail.rowsTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('reconciliation.runDetail.rowsHint')}</p>
            </div>

            <ReconciliationListControls onApply={onApplyFilters} onReset={onResetFilters}>
              <ReconciliationAssetNetworkFilter
                id="run-rows-asset"
                label={t('reconciliation.filters.asset')}
                value={filterDraft.assetCode}
                options={assetOptions}
                disabled={currenciesLoading}
                onChange={onAssetFilterChange}
              />
              <ReconciliationAssetNetworkFilter
                id="run-rows-network"
                label={t('reconciliation.filters.network')}
                value={filterDraft.networkCode}
                options={networkOptionsForAsset}
                disabled={currenciesLoading}
                onChange={onNetworkFilterChange}
              />
            </ReconciliationListControls>

            <div
              className="scrollbar-thin modal-scroll-pane min-h-0 flex-1 overflow-y-auto py-px [scrollbar-gutter:stable]"
            >
              <ReconciliationTableShell
                embedded
                isLoading={rowsLoading}
                isEmpty={!rowsLoading && rows.length === 0}
                emptyMessage={t('reconciliation.runDetail.rowsEmpty')}
              >
                <Table className={reconciliationRunRowsTableClass}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('reconciliation.rows.scope')}</TableHead>
                      <TableHead>{t('reconciliation.rows.observed')}</TableHead>
                      <TableHead>{t('reconciliation.rows.ledger')}</TableHead>
                      <TableHead>{t('reconciliation.rows.diff')}</TableHead>
                      <TableHead>{t('reconciliation.rows.completeness')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <RunRow key={row.id} row={row} t={t} />
                    ))}
                  </TableBody>
                </Table>
              </ReconciliationTableShell>
            </div>
            <ReconciliationTablePagination pagination={rowsPagination} onPageChange={onRowsPageChange} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RunRow({ row, t }: { row: ReconciliationSnapshotDto; t: TFunction<'cabinet'> }) {
  return (
    <TableRow>
      <TableCell className="max-w-[12rem] truncate font-mono text-xs">{row.scopeKey}</TableCell>
      <TableCell className="tabular-nums">
        {observedBalance(row.values)
          ? formatLedgerAmount(observedBalance(row.values)!, { assetLabel: row.assetCode })
          : t('balance.amountUnknown')}
      </TableCell>
      <TableCell className="tabular-nums">
        {snapshotBalance(row.values, 'ledgerBalance')
          ? formatLedgerAmount(snapshotBalance(row.values, 'ledgerBalance')!, { assetLabel: row.assetCode })
          : '—'}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.difference ? formatLedgerAmount(row.difference, { assetLabel: row.assetCode }) : '—'}
      </TableCell>
      <TableCell>{completenessLabel(t, row.completeness)}</TableCell>
    </TableRow>
  );
}
